/**
 * Admin permissions: single source of truth for admin access checks on the frontend.
 *
 * All admin route guards, nav visibility, and section visibility use these pure
 * functions so permission rules are defined in one place.
 *
 * Admin routes and access:
 * - /admin — entry: any of admin.access, admin.full, payments.verify, tickets.scan, tickets.issue, raffle.manage.
 * - /admin/orders/cash-issue — operator only (payments.verify | admin.full). Cash ticket issuance in order context.
 * - /admin/tickets/cash — tickets.issue | admin.full. For roles that can only issue tickets (no payments access).
 */

// =============================================================================
// PERMISSION CONSTANTS
// =============================================================================

/**
 * Well-known permission codes used for access control.
 * Aligned with backend auth.service and GraphQL schema.
 */
export const PERMISSIONS = {
  /** Full admin access - can manage everything */
  ADMIN_FULL: "admin.full",
  /** Can access admin panel (entry point) */
  ADMIN_ACCESS: "admin.access",
  /** Can verify/approve payments */
  PAYMENTS_VERIFY: "payments.verify",
  /** Can issue tickets */
  TICKETS_ISSUE: "tickets.issue",
  /** Can scan/validate tickets */
  TICKETS_SCAN: "tickets.scan",
  /** Can manage raffles */
  RAFFLE_MANAGE: "raffle.manage",
  /** Can manage gender party voting */
  GENDER_PARTY_MANAGE: "gender_party.manage",
  /** Can manage academy courses and quizzes */
  ACADEMY_MANAGE: "academy.manage",
} as const;

/** Any of these grants access to admin panel home (/admin). Same set as backend canAccessAdmin. */
export const ADMIN_PANEL_ACCESS_PERMISSIONS = [
  PERMISSIONS.ADMIN_ACCESS,
  PERMISSIONS.ADMIN_FULL,
  PERMISSIONS.PAYMENTS_VERIFY,
  PERMISSIONS.TICKETS_SCAN,
  PERMISSIONS.TICKETS_ISSUE,
  PERMISSIONS.RAFFLE_MANAGE,
  PERMISSIONS.GENDER_PARTY_MANAGE,
  PERMISSIONS.ACADEMY_MANAGE,
] as const;

// =============================================================================
// ACCESS LEVEL TYPE
// =============================================================================

/** Access level used by admin route guards. */
export type AdminAccessLevel =
  | "admin-panel"
  | "operator"
  | "admin"
  | "raffle"
  | "tickets"
  | "tickets-issue"
  | "gender-party"
  | "academy";

// =============================================================================
// PURE ACCESS CHECK FUNCTIONS
// =============================================================================

/** True if user has any permission that grants access to /admin. */
export function hasAdminPanelAccess(permissions: string[] | undefined): boolean {
  if (!permissions?.length) return false;
  return ADMIN_PANEL_ACCESS_PERMISSIONS.some((p) => permissions.includes(p));
}

/** Operator = payments.verify OR admin.full. */
export function hasOperatorAccess(permissions: string[] | undefined): boolean {
  if (!permissions?.length) return false;
  return (
    permissions.includes(PERMISSIONS.PAYMENTS_VERIFY) ||
    permissions.includes(PERMISSIONS.ADMIN_FULL)
  );
}

/** Admin = admin.full only. */
export function hasAdminAccess(permissions: string[] | undefined): boolean {
  if (!permissions?.length) return false;
  return permissions.includes(PERMISSIONS.ADMIN_FULL);
}

/** Raffle = raffle.manage OR admin.full. */
export function hasRaffleAccess(permissions: string[] | undefined): boolean {
  if (!permissions?.length) return false;
  return (
    permissions.includes(PERMISSIONS.RAFFLE_MANAGE) ||
    permissions.includes(PERMISSIONS.ADMIN_FULL)
  );
}

/** Gender Party = gender_party.manage OR admin.full. */
export function hasGenderPartyAccess(permissions: string[] | undefined): boolean {
  if (!permissions?.length) return false;
  return (
    permissions.includes(PERMISSIONS.GENDER_PARTY_MANAGE) ||
    permissions.includes(PERMISSIONS.ADMIN_FULL)
  );
}

/** Academy = academy.manage OR admin.full. */
export function hasAcademyAccess(permissions: string[] | undefined): boolean {
  if (!permissions?.length) return false;
  return (
    permissions.includes(PERMISSIONS.ACADEMY_MANAGE) ||
    permissions.includes(PERMISSIONS.ADMIN_FULL)
  );
}

/** Tickets = tickets.scan OR tickets.issue OR admin.full. */
export function hasTicketAccess(permissions: string[] | undefined): boolean {
  if (!permissions?.length) return false;
  return (
    permissions.includes(PERMISSIONS.TICKETS_SCAN) ||
    permissions.includes(PERMISSIONS.TICKETS_ISSUE) ||
    permissions.includes(PERMISSIONS.ADMIN_FULL)
  );
}

/** Ticket issue only = tickets.issue OR admin.full. */
export function hasTicketIssueAccess(permissions: string[] | undefined): boolean {
  if (!permissions?.length) return false;
  return (
    permissions.includes(PERMISSIONS.TICKETS_ISSUE) ||
    permissions.includes(PERMISSIONS.ADMIN_FULL)
  );
}

/**
 * Check if permissions satisfy the given admin access level.
 * Used by AdminRouteGuard and (via guards.ts) by TanStack Router when migrated.
 */
export function checkAdminAccess(
  level: AdminAccessLevel,
  permissions: string[] | undefined
): boolean {
  switch (level) {
    case "admin-panel":
      return hasAdminPanelAccess(permissions);
    case "operator":
      return hasOperatorAccess(permissions);
    case "admin":
      return hasAdminAccess(permissions);
    case "raffle":
      return hasRaffleAccess(permissions);
    case "tickets":
      return hasTicketAccess(permissions);
    case "tickets-issue":
      return hasTicketIssueAccess(permissions);
    case "gender-party":
      return hasGenderPartyAccess(permissions);
    case "academy":
      return hasAcademyAccess(permissions);
  }
}
